// useAiI18n — registers the Rheora AI module's strings into the `@fluxora/i18n`
// in-memory resource store on mount.
//
// Mirrors `useMarketOpsI18n` / `useQuotationI18n` exactly, including the
// outer-key strip: `en.json` is `{ ai: { … } }` and the namespace registered
// with i18n is `ai`, so a key reads `ai.panel.title` with the namespace
// resolved from the leading segment.
//
// Called once from `AiAssistantProvider`, which is mounted above the router —
// so every AI surface (dock, launcher, live region) has its strings regardless
// of which route the operator is on when they open the panel. Components
// rendered in isolation by a unit test will render raw keys unless the test
// wraps them in something that calls this hook; that is deliberate, because a
// component reaching for the registrar itself would re-register the whole
// dictionary on every mount.

import { useEffect } from 'react';

import { useTranslation } from '@fluxora/i18n';

import en from '../locales/en.json';

/** Flatten a nested object's leaf strings into "dot.path" keys. */
function flatten(obj: unknown, prefix: string, out: Record<string, string>): void {
  if (obj === null || typeof obj !== 'object') return;
  for (const [k, v] of Object.entries(obj as Record<string, unknown>)) {
    const next = prefix === '' ? k : `${prefix}.${k}`;
    if (typeof v === 'string') {
      out[next] = v;
    } else if (typeof v === 'object' && v !== null) {
      flatten(v, next, out);
    }
  }
}

export function useAiI18n(): void {
  const { addResources, locale } = useTranslation();
  useEffect(() => {
    const flat: Record<string, string> = {};
    const dict = (en as { ai?: unknown }).ai;
    if (dict !== undefined) {
      flatten(dict, '', flat);
    }
    addResources(locale, 'ai', flat);
  }, [addResources, locale]);
}
